import { createAsyncThunk } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import mixpanel from 'mixpanel-browser';
import FEATURES from 'src/featureMap';
import { ScanLogResponse } from 'src/types/scanLogTypes';

const AUTH_SESSION_KEY = 'authed';

function getAccountName(): string {
	try {
		const authed = sessionStorage.getItem(AUTH_SESSION_KEY);
		if (authed) {
			const parsed = JSON.parse(authed);
			return parsed?.accountDetails?.account?.name ?? '';
		}
	} catch {
		// ignore parse errors
	}
	return '';
}

function trackScanLogsFetch({
	accountId,
	searchText,
	searchField,
	filters,
	page,
	pageSize,
	sort,
	sortDir,
	startDate,
	endDate,
	timeTaken,
	statusCode,
}: {
	accountId: string;
	searchText: string;
	searchField: string;
	filters: string[];
	page: number;
	pageSize: number;
	sort: string;
	sortDir: string;
	startDate: string | null;
	endDate: string | null;
	timeTaken: number;
	statusCode: number;
}) {
	const action = FEATURES.scanLogsFetch;
	const trackingData = {
		apiType: 'scanlogs',
		params: [
			searchText ? `searchText:${searchText}` : '',
			searchField ? `searchField:${searchField}` : '',
			filters.length > 0 ? `filters:${filters.join(',')}` : '',
			`page:${page}`,
			`pageSize:${pageSize}`,
			sort ? `sort:${sort}` : '',
			sortDir ? `sortDir:${sortDir}` : '',
			startDate ? `startDate:${startDate}` : '',
			endDate ? `endDate:${endDate}` : '',
		]
			.filter(Boolean)
			.join('|'),
		timeTakenInMs: timeTaken,
		statusCode,
		accountId,
		accountName: getAccountName(),
	};
	mixpanel.track(`DI ${action.id}`, trackingData);
}

export const fetchESScans = createAsyncThunk<
	ScanLogResponse,
	FetchESScansPayload,
	{ rejectValue: string }
>(
	'scanlogs/fetchESScans',
	async (
		{
			api,
			id,
			startDate,
			endDate,
			searchText,
			searchField,
			filters,
			page,
			pageSize,
			sort,
			sortDir,
		},
		{ rejectWithValue },
	) => {
		const formattedStartDate = startDate
			? DateTime.fromISO(startDate)
					.toUTC()
					.toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		const formattedEndDate = endDate
			? DateTime.fromISO(endDate).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		const startTime = performance.now();

		try {
			const result = await api.getAccountESScansData(
				id,
				formattedStartDate,
				formattedEndDate,
				searchText,
				searchField,
				filters,
				page,
				pageSize,
				sort,
				sortDir,
			);

			trackScanLogsFetch({
				accountId: id,
				searchText,
				searchField,
				filters,
				page,
				pageSize,
				sort,
				sortDir,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				timeTaken: Math.round(performance.now() - startTime),
				statusCode: 200,
			});

			return result;
		} catch (error) {
			const statusCode =
				error &&
				typeof error === 'object' &&
				'response' in error &&
				(error as any).response?.status
					? (error as any).response.status
					: 0;

			trackScanLogsFetch({
				accountId: id,
				searchText,
				searchField,
				filters,
				page,
				pageSize,
				sort,
				sortDir,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				timeTaken: Math.round(performance.now() - startTime),
				statusCode,
			});

			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			return rejectWithValue(message);
		}
	},
);

type FetchESScansPayload = {
	api: {
		getAccountESScansData: (
			id: string,
			startDate: string,
			endDate: string,
			searchText: string,
			searchField: string,
			filters: string[],
			page: number,
			pageSize: number,
			sort: string,
			sortDir: string,
		) => Promise<ScanLogResponse>;
	};
	id: string;
	startDate: string;
	endDate: string;
	searchText: string;
	searchField: string;
	filters: string[];
	page: number;
	pageSize: number;
	sort: string;
	sortDir: string;
};
